import { Box, VStack } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { FC, useMemo } from "react";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";
import { FormikTextareaField } from "~/src/components/common/forms/FormikTextareaField";
import { FormikTextField } from "~/src/components/common/forms/FormikTextField";

import { popupImportSelectWithMenuPortalProps } from "../popupImportSelectMenuPortal";
import type { PopupImportFormValues } from "../validationSchema";

const TIMING_VALUE_KINDS = new Set<string>([
  "DURATION",
  "SCROLL_RATE",
  "EXIT_AND_SCROLL_RATE_1",
  "EXIT_AND_SCROLL_RATE_2",
  "EXIT_AND_SCROLL_RATE_3",
  "SCROLL_PIXEL",
]);

export type Props = {
  popupIndex: number;
};

export const PopupImportActionTimingCell: FC<Props> = ({ popupIndex }) => {
  const { values } = useFormikContext<PopupImportFormValues>();
  const actionKind =
    values.popups[popupIndex]?.popup?.actionKindRaw?.trim() ?? "";

  const timingSuffix = useMemo(() => {
    if (actionKind === "DURATION") return "秒";
    if (
      actionKind === "SCROLL_RATE" ||
      actionKind === "EXIT_AND_SCROLL_RATE_1" ||
      actionKind === "EXIT_AND_SCROLL_RATE_2" ||
      actionKind === "EXIT_AND_SCROLL_RATE_3"
    )
      return "%";
    if (actionKind === "SCROLL_PIXEL") return "px";
    return undefined;
  }, [actionKind]);

  const base = `popups.${popupIndex}.popup`;

  return (
    <VStack align="stretch" spacing="8px" minW="180px" maxW="280px">
      <FormikSelectField
        name={`${base}.actionKindRaw`}
        options={GraphQLEnums.PopupActionKind}
        {...popupImportSelectWithMenuPortalProps}
      />
      {TIMING_VALUE_KINDS.has(actionKind) ? (
        <Box>
          <FormikTextField
            mode="fill"
            type="number"
            name={`${base}.quitTiming`}
            placeholder="0"
            rightAdornment={timingSuffix}
          />
        </Box>
      ) : null}
      {actionKind === "SCROLL_RATE_RANGE" ? (
        <FormikTextField
          mode="fill"
          type="text"
          name={`${base}.scrollRateRangeRaw`}
          placeholder="0~0"
        />
      ) : null}
      {actionKind === "CUSTOM_EVENT" ? (
        <FormikTextareaField
          mode="fill"
          name={`${base}.customEvent`}
          placeholder="js"
          resize="vertical"
        />
      ) : null}
    </VStack>
  );
};
